import type { EntryProject, Position } from '../types';

export interface BreakSelectionContext {
  positions?: Position[];
  isBreakRelevant?: (projectId: string) => boolean;
}

export type BreakContextParam = Position[] | BreakSelectionContext | ((projectId: string) => boolean);

export interface CalculatedBlock {
  projectId: string;
  description: string;
  timeArrived: string; // HH:MM
  timeLeft: string; // HH:MM
  breakDurationMinutes: number;
  workMinutes?: number;
}

export type TimeBlock = CalculatedBlock;

export const parseTime = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

export const formatTime = (minutesFromMidnight: number): string => {
  if (isNaN(minutesFromMidnight)) return '00:00';
  const totalMinutes = Math.max(0, Math.round(minutesFromMidnight));
  const hours = Math.floor(totalMinutes / 60) % 24;
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

export const formatDuration = (minutes: number): string => {
  const roundedMins = Math.round(minutes);
  const h = Math.floor(roundedMins / 60);
  const m = roundedMins % 60;
  return `${h}h ${m}m`;
};

export const formatDecimalHours = (minutes: number): string => {
  const safeMins = Math.max(0, minutes);
  const hours = safeMins / 60;
  const rounded = Math.round(hours * 100) / 100;
  return `${rounded}h`;
};

export const formatHoursWithDuration = (minutes: number): string => {
  const safeMins = Math.max(0, minutes);
  return `${formatDecimalHours(safeMins)} (${formatDuration(safeMins)})`;
};

/**
 * Parses user input in various formats to absolute minutes:
 * - "8h 17m", "8h17m", "8h 17", "8h17", "8 hours 17 mins", "8 hrs 15 min" -> 497
 * - "8:17", "08:17", "1:30" -> 497, 90
 * - "17m", "17 min", "17 mins", "497m", "45 minutes" -> 17, 497, 45
 * - "8h", "8 hrs", "8 hours", "1.5h", "1,5 hours" -> 480, 90
 * - "8.5", "8,5", "8.28", "8" -> 510, 510, 497, 480 (plain numbers interpreted as hours)
 * Returns null if the format is invalid or unrecognized.
 */
export const parseDurationInput = (input: string): number | null => {
  if (!input) return null;
  const trimmed = input.trim().toLowerCase();
  if (!trimmed) return null;

  // 1. Colon notation (e.g., "8:17", "08:17", "1:30")
  const colonMatch = trimmed.match(/^(\d{1,2}):([0-5]?\d)$/);
  if (colonMatch) {
    const h = parseInt(colonMatch[1], 10);
    const m = parseInt(colonMatch[2], 10);
    return h * 60 + m;
  }

  // 2. Explicit hours and minutes combined: "8h 17m", "8h17m", "8h 17", "8h17", "8 hours 17 mins", "8 hrs 15 min"
  const hmMatch = trimmed.match(/^(\d+)\s*h[a-z]*\s*(\d+)(?:\s*m[a-z]*)?$/i);
  if (hmMatch) {
    const h = parseInt(hmMatch[1], 10);
    const m = parseInt(hmMatch[2], 10);
    return h * 60 + m;
  }

  // 3. Minutes only: "17m", "17 min", "17 mins", "497m", "45 minutes"
  const mMatch = trimmed.match(/^(\d+)\s*(?:m|mins?|minutes?)$/);
  if (mMatch) {
    return parseInt(mMatch[1], 10);
  }

  // 4. Hours only (with unit): "8h", "8 hrs", "8 hours", "1.5h", "1,5 hours"
  const hMatch = trimmed.match(/^(\d+(?:[.,]\d+)?)\s*(?:h|hrs?|hours?)$/);
  if (hMatch) {
    const num = parseFloat(hMatch[1].replace(',', '.'));
    if (!isNaN(num)) return Math.round(num * 60 * 100) / 100;
  }

  // 5. Raw decimal or integer numbers without unit: "8.5", "8,5", "8.28", "8" -> interpret as hours
  const numMatch = trimmed.match(/^(\d+(?:[.,]\d+)?)$/);
  if (numMatch) {
    const num = parseFloat(numMatch[1].replace(',', '.'));
    if (!isNaN(num)) return Math.round(num * 60 * 100) / 100;
  }

  return null;
};

/**
 * Parses user input for a clock time (e.g., "09:30", "9:30", "9.30", "0930", "9")
 * into minutes from midnight (0 - 1439).
 * Returns null if the format or time values are invalid.
 */
export const parseTimeInput = (input: string): number | null => {
  if (!input) return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  // 1. Standard colon: "9:30", "09:30", "14:05"
  const colonMatch = trimmed.match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
  if (colonMatch) {
    const h = parseInt(colonMatch[1], 10);
    const m = parseInt(colonMatch[2], 10);
    return h * 60 + m;
  }

  // 2. Dot notation: "9.30", "09.30", "14.05"
  const dotMatch = trimmed.match(/^([01]?\d|2[0-3])\.([0-5]\d)$/);
  if (dotMatch) {
    const h = parseInt(dotMatch[1], 10);
    const m = parseInt(dotMatch[2], 10);
    return h * 60 + m;
  }

  // 3. Compact 3 or 4 digits: "930" -> 09:30, "0930" -> 09:30, "1430" -> 14:30
  const compactMatch = trimmed.match(/^(\d{1,2})([0-5]\d)$/);
  if (compactMatch) {
    const h = parseInt(compactMatch[1], 10);
    const m = parseInt(compactMatch[2], 10);
    if (h >= 0 && h <= 23) {
      return h * 60 + m;
    }
  }

  // 4. Plain hour: "9", "09", "14" (interpreted as 9:00, 14:00)
  const hourMatch = trimmed.match(/^([01]?\d|2[0-3])$/);
  if (hourMatch) {
    const h = parseInt(hourMatch[1], 10);
    return h * 60;
  }

  return null;
};


export const calculateTotalWorkMinutes = (arrived: string, left: string, breakMins: number): number => {
  const arrivedMins = parseTime(arrived);
  let leftMins = parseTime(left);
  if (leftMins < arrivedMins) leftMins += 24 * 60;
  return leftMins - arrivedMins - breakMins;
};

/**
 * Calculates total span in minutes between arrival and target departure,
 * handling overnight shifts past midnight.
 * Returns null if arrival or targetDeparture is not a valid 24h time format.
 */
export const calculateTargetSpanMinutes = (arrived: string, targetDeparture: string): number | null => {
  const timeRegex = /^([01]?\d|2[0-3]):[0-5]\d$/;
  if (!arrived || !targetDeparture || !timeRegex.test(arrived.trim()) || !timeRegex.test(targetDeparture.trim())) {
    return null;
  }
  const arrivedMins = parseTime(arrived);
  let targetMins = parseTime(targetDeparture);
  if (targetMins < arrivedMins) {
    targetMins += 24 * 60;
  }
  return targetMins - arrivedMins;
};

function resolveBreakRelevance(
  projectId: string,
  context?: BreakContextParam
): boolean {
  if (!context) return false;
  if (typeof context === 'function') {
    return Boolean(context(projectId));
  }
  if (Array.isArray(context)) {
    return Boolean(context.find(p => (p.id === projectId || p.projectId === projectId))?.breakRelevant);
  }
  if (typeof context === 'object') {
    if (context.isBreakRelevant) {
      return Boolean(context.isBreakRelevant(projectId));
    }
    if (context.positions) {
      return Boolean(context.positions.find(p => (p.id === projectId || p.projectId === projectId))?.breakRelevant);
    }
  }
  return false;
}

export function calculateTimeBlocks(
  timeArrived: string,
  timeLeft: string,
  breakDurationMinutes: number,
  projects: EntryProject[],
  breakContext?: BreakContextParam
): CalculatedBlock[];
export function calculateTimeBlocks(
  timeArrived: string,
  timeLeft: string,
  breakDurationMinutes: number,
  legacyMainProjectId: string,
  projects: EntryProject[],
  breakContext?: BreakContextParam
): CalculatedBlock[];
export function calculateTimeBlocks(
  timeArrived: string,
  timeLeft: string,
  breakDurationMinutes: number,
  arg4: EntryProject[] | string,
  arg5?: EntryProject[] | BreakContextParam,
  arg6?: BreakContextParam
): CalculatedBlock[] {
  const arrivedMins = parseTime(timeArrived);
  let leftMins = parseTime(timeLeft);
  
  if (leftMins < arrivedMins) {
    // Crossed midnight, add 24 hours
    leftMins += 24 * 60;
  }

  const elapsedMins = leftMins - arrivedMins;
  const totalWorkMins = elapsedMins - breakDurationMinutes;

  if (totalWorkMins < 0) return []; // Invalid

  const inputProjects: EntryProject[] = Array.isArray(arg4) ? arg4 : (Array.isArray(arg5) ? (arg5 as EntryProject[]) : []);
  const workingProjects = inputProjects.filter(p => p.projectId !== 'break');
  if (workingProjects.length === 0) return [];

  const breakContext = (Array.isArray(arg4) ? arg5 : arg6) as BreakContextParam | undefined;

  // Step 1: Calculate rounded work minutes for each block
  const blockMinutes = workingProjects.map(proj => {
    if (typeof proj.minutes === 'number' && proj.minutes > 0 && !proj.percentage) {
      return proj.minutes;
    }
    const rawWorkMins = proj.minutes ?? (totalWorkMins * ((proj.percentage || 0) / 100));
    return Math.round(rawWorkMins / 15) * 15;
  });

  // Step 2: Sum work minutes per position for this day and check break relevance
  interface ProjectSummary {
    projectId: string;
    totalMinutes: number;
    firstBlockIndex: number;
    isBreakRelevant: boolean;
  }

  const summaries = new Map<string, ProjectSummary>();

  for (let i = 0; i < workingProjects.length; i++) {
    const proj = workingProjects[i];
    const mins = blockMinutes[i];
    const isRelevant = Boolean(proj.breakRelevant || resolveBreakRelevance(proj.projectId, breakContext));

    const existing = summaries.get(proj.projectId);
    if (existing) {
      existing.totalMinutes += mins;
      if (isRelevant) existing.isBreakRelevant = true;
    } else {
      summaries.set(proj.projectId, {
        projectId: proj.projectId,
        totalMinutes: mins,
        firstBlockIndex: i,
        isBreakRelevant: isRelevant
      });
    }
  }

  // Step 3: Determine which position(s) receive the break
  // Primary: find the maximum totalMinutes logged on this day across all positions
  let maxMinutes = -1;
  for (const summary of summaries.values()) {
    if (summary.totalMinutes > maxMinutes) {
      maxMinutes = summary.totalMinutes;
    }
  }

  // All positions tied for maximum totalMinutes
  const topSummaries = Array.from(summaries.values()).filter(s => s.totalMinutes === maxMinutes);

  // Check which top positions are flagged as break-relevant
  const breakRelevantTop = topSummaries.filter(s => s.isBreakRelevant);

  // Winning positions:
  // - If multiple top positions are break-relevant: split the break hours evenly among them
  // - If exactly one top position is break-relevant: it absorbs the entire break
  // - If none of the top positions are break-relevant: the first position in order absorbs the entire break
  const winningSummaries = breakRelevantTop.length > 0 ? breakRelevantTop : [topSummaries[0]];

  // Distribute breakDurationMinutes among winning positions
  const breakAllocationByBlock = new Map<number, number>();

  if (winningSummaries.length > 0 && breakDurationMinutes > 0) {
    const count = winningSummaries.length;
    if (count === 1) {
      breakAllocationByBlock.set(winningSummaries[0].firstBlockIndex, breakDurationMinutes);
    } else if (Number.isInteger(breakDurationMinutes)) {
      const baseShare = Math.floor(breakDurationMinutes / count);
      const remainder = breakDurationMinutes % count;

      winningSummaries.forEach((summary, idx) => {
        const share = baseShare + (idx < remainder ? 1 : 0);
        breakAllocationByBlock.set(summary.firstBlockIndex, share);
      });
    } else {
      const share = Math.round((breakDurationMinutes / count) * 100) / 100;
      winningSummaries.forEach((summary) => {
        breakAllocationByBlock.set(summary.firstBlockIndex, share);
      });
    }
  }

  let currentStartMins = arrivedMins;
  const blocks: CalculatedBlock[] = [];

  for (let i = 0; i < workingProjects.length; i++) {
    const proj = workingProjects[i];
    const workMins = blockMinutes[i];
    const blockBreak = breakAllocationByBlock.get(i) || 0;
    const blockDuration = workMins + blockBreak;
    
    const blockEndMins = currentStartMins + blockDuration;

    blocks.push({
      projectId: proj.projectId,
      description: proj.description,
      timeArrived: formatTime(currentStartMins),
      timeLeft: formatTime(blockEndMins),
      breakDurationMinutes: blockBreak,
      workMinutes: workMins,
    });

    currentStartMins = blockEndMins;
  }

  // Address rounding error on the last block
  if (blocks.length > 0) {
    blocks[blocks.length - 1].timeLeft = formatTime(leftMins);
  }

  return blocks;
}

export const consolidateProjects = (projectsList: EntryProject[]): EntryProject[] => {
  const consolidatedMap = new Map<string, number>();
  const descriptionsMap = new Map<string, string[]>();

  projectsList.forEach(p => {
    if (p.projectId === 'break') return;
    consolidatedMap.set(p.projectId, (consolidatedMap.get(p.projectId) || 0) + p.minutes);
    if (p.description && p.description.trim() !== '') {
      const descs = descriptionsMap.get(p.projectId) || [];
      const trimmed = p.description.trim();
      if (!descs.includes(trimmed)) {
        descs.push(trimmed);
      }
      descriptionsMap.set(p.projectId, descs);
    }
  });

  return Array.from(consolidatedMap.entries()).map(([projectId, minutes]) => {
    const descs = descriptionsMap.get(projectId) || [];
    return {
      projectId,
      minutes,
      description: descs.join(', ')
    };
  });
};

export interface PositionTotal {
  projectId: string;
  name: string;
  minutes: number;
}
export type ProjectTotal = PositionTotal;

export const getEntryProjectMinutes = (
  entry: {
    timeArrived?: string;
    timeLeft?: string;
    breakDurationMinutes?: number;
    projects: EntryProject[];
  },
  projectId: string
): number => {
  let mins = 0;
  entry.projects.forEach(p => {
    if (p.projectId === projectId) {
      if (typeof p.minutes === 'number' && p.minutes > 0) {
        mins += p.minutes;
      } else if (p.percentage) {
        const entryTotal = Math.max(
          0,
          calculateTotalWorkMinutes(entry.timeArrived || '', entry.timeLeft || '', entry.breakDurationMinutes || 0)
        );
        mins += Math.round((entryTotal * p.percentage) / 100);
      }
    }
  });
  return mins;
};

export const calculateProjectTotals = (
  entries: Array<{
    timeArrived?: string;
    timeLeft?: string;
    breakDurationMinutes?: number;
    projects: EntryProject[];
  }>,
  getProjectName: (id: string) => string
): PositionTotal[] => {
  const projectTotalsMap: Record<string, number> = {};
  entries.forEach(entry => {
    entry.projects.forEach(p => {
      if (p.projectId === 'break') return;
      let mins = 0;
      if (typeof p.minutes === 'number' && p.minutes > 0) {
        mins += p.minutes;
      } else if (p.percentage) {
        const entryTotal = Math.max(
          0,
          calculateTotalWorkMinutes(entry.timeArrived || '', entry.timeLeft || '', entry.breakDurationMinutes || 0)
        );
        mins += Math.round((entryTotal * p.percentage) / 100);
      }
      if (mins > 0) {
        projectTotalsMap[p.projectId] = (projectTotalsMap[p.projectId] || 0) + mins;
      }
    });
  });

  return Object.entries(projectTotalsMap)
    .map(([projectId, minutes]) => ({
      projectId,
      name: getProjectName(projectId),
      minutes
    }))
    .sort((a, b) => b.minutes - a.minutes);
};

export const calculatePositionTotals = calculateProjectTotals;
export const getEntryPositionMinutes = getEntryProjectMinutes;

export const getEntryWorkMinutes = (entry: {
  timeArrived?: string;
  timeLeft?: string;
  breakDurationMinutes?: number;
  projects?: EntryProject[];
}): number => {
  const activeProjects = (entry.projects || []).filter(p => p.projectId !== 'break');
  const projectTotal = activeProjects.reduce((sum, p) => {
    if (typeof p.minutes === 'number' && p.minutes > 0) {
      return sum + p.minutes;
    }
    return sum;
  }, 0);

  if (projectTotal > 0) {
    return Math.round(projectTotal * 100) / 100;
  }

  return calculateTotalWorkMinutes(
    entry.timeArrived || '',
    entry.timeLeft || '',
    entry.breakDurationMinutes || 0
  );
};

export const calculateMinutesToDays = (minutes: number): number => {
  return (minutes / 60) / 8.4;
};

export type BudgetThresholdStatus = 'normal' | 'warning' | 'critical';

export const getBudgetThresholdStatus = (
  consumed: number,
  target: number,
  warningThresholdPercent: number = 80
): BudgetThresholdStatus => {
  if (target <= 0) return 'normal';
  const percent = (consumed / target) * 100;
  if (percent > 100) return 'critical';
  if (percent >= warningThresholdPercent) return 'warning';
  return 'normal';
};

export interface BudgetConsumptionResult {
  totalMinutes: number;
  totalDays: number;
  remainingDays: number;
  percentConsumed: number;
  nominalShareDays: number;
  memberDays: Record<string, number>;
  teamThresholdStatus: BudgetThresholdStatus;
}

const isEntryInBudgetScope = (
  entry: { date: string; userId?: string },
  startDate?: string,
  endDate?: string,
  membersSet?: Set<string> | null
): boolean => {
  if (startDate && entry.date < startDate) return false;
  if (endDate && entry.date > endDate) return false;
  if (membersSet && entry.userId && !membersSet.has(entry.userId)) return false;
  return true;
};

export const calculateBudgetConsumption = (
  entries: Array<{
    date: string;
    userId?: string;
    projects: EntryProject[];
  }>,
  budget: {
    id?: string;
    projectId?: string;
    startDate?: string;
    endDate?: string;
    budgetDays?: number;
    contingentDays?: number;
    warningThresholdPercent?: number;
    members?: string[];
  }
): BudgetConsumptionResult => {
  let totalMinutes = 0;
  const memberMinutes: Record<string, number> = {};
  const membersSet = budget.members && budget.members.length > 0 ? new Set(budget.members) : null;
  const targetId = budget.id || budget.projectId || '';
  const totalPoolDays = budget.contingentDays ?? budget.budgetDays ?? 0;

  for (const entry of entries) {
    if (!isEntryInBudgetScope(entry, budget.startDate, budget.endDate, membersSet)) continue;

    for (const p of entry.projects) {
      const entryPosId = ('positionId' in p && typeof p.positionId === 'string' ? p.positionId : p.projectId);
      if (entryPosId === targetId || (budget.projectId && entryPosId === budget.projectId)) {
        const mins = p.minutes || 0;
        totalMinutes += mins;
        if (entry.userId) {
          memberMinutes[entry.userId] = (memberMinutes[entry.userId] || 0) + mins;
        }
      }
    }
  }

  const totalDays = calculateMinutesToDays(totalMinutes);
  const remainingDays = Math.max(0, totalPoolDays - totalDays);
  const percentConsumed = totalPoolDays > 0 ? (totalDays / totalPoolDays) * 100 : 0;
  const memberCount = budget.members && budget.members.length > 0 ? budget.members.length : 0;
  const nominalShareDays = memberCount > 0 ? totalPoolDays / memberCount : 0;

  const memberDays: Record<string, number> = {};
  for (const [uid, mins] of Object.entries(memberMinutes)) {
    memberDays[uid] = calculateMinutesToDays(mins);
  }

  const teamThresholdStatus = getBudgetThresholdStatus(
    totalDays,
    totalPoolDays,
    budget.warningThresholdPercent ?? 80
  );

  return {
    totalMinutes,
    totalDays,
    remainingDays,
    percentConsumed,
    nominalShareDays,
    memberDays,
    teamThresholdStatus
  };
};

export const calculatePositionConsumption = calculateBudgetConsumption;
export const consolidatePositions = consolidateProjects;

export const calculateUserNominalProgress = (
  userDays: number,
  nominalShareDays: number,
  warningThresholdPercent: number = 80
): {
  userDays: number;
  nominalShareDays: number;
  percentOfShare: number;
  status: BudgetThresholdStatus;
} => {
  const percentOfShare = nominalShareDays > 0 ? (userDays / nominalShareDays) * 100 : 0;
  const status = getBudgetThresholdStatus(userDays, nominalShareDays, warningThresholdPercent);
  return {
    userDays,
    nominalShareDays,
    percentOfShare,
    status
  };
};

export interface WorkingDaysResult {
  workingDays: string[];
  weekendCount: number;
  totalDays: number;
}

export const getWorkingDaysInRange = (startDateStr: string, endDateStr: string): WorkingDaysResult => {
  if (!startDateStr || !endDateStr || startDateStr > endDateStr) {
    return { workingDays: [], weekendCount: 0, totalDays: 0 };
  }

  const [sYear, sMonth, sDay] = startDateStr.split('-').map(Number);
  const [eYear, eMonth, eDay] = endDateStr.split('-').map(Number);

  const current = new Date(sYear, sMonth - 1, sDay);
  const end = new Date(eYear, eMonth - 1, eDay);

  const workingDays: string[] = [];
  let weekendCount = 0;
  let totalDays = 0;

  while (current <= end) {
    totalDays++;
    const dayOfWeek = current.getDay();
    const formatted = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`;
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      weekendCount++;
    } else {
      workingDays.push(formatted);
    }
    current.setDate(current.getDate() + 1);
  }

  return { workingDays, weekendCount, totalDays };
};
